/**
 * AnomalyAlerts - Display detected anomalies as alert banner
 * 
 * Shows critical, warning, and info level anomalies
 * Expandable to show details
 */

import React, { useState } from 'react';
import { AlertTriangle, AlertCircle, Info, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Anomaly } from '../lib/anomaly-detection';

interface AnomalyAlertsProps {
  anomalies: Anomaly[];
}

export function AnomalyAlerts({ anomalies }: AnomalyAlertsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  if (anomalies.length === 0 || isDismissed) {
    return null;
  }

  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  const warningAnomalies = anomalies.filter(a => a.severity === 'warning');
  const infoAnomalies = anomalies.filter(a => a.severity === 'info');

  const getSeverityColor = (severity: Anomaly['severity']) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-900',
          icon: 'text-red-600',
          badge: 'bg-red-100 text-red-800'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-900',
          icon: 'text-amber-600',
          badge: 'bg-amber-100 text-amber-800'
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          icon: 'text-blue-600',
          badge: 'bg-blue-100 text-blue-800'
        };
    }
  };

  const getIcon = (severity: Anomaly['severity']) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
    }
  };

  // Determine overall severity for banner
  const overallSeverity = criticalAnomalies.length > 0 ? 'critical' : 
                         warningAnomalies.length > 0 ? 'warning' : 'info';
  const colors = getSeverityColor(overallSeverity);

  return (
    <div className={`${colors.bg} border-2 ${colors.border} rounded-xl overflow-hidden`}>
      {/* Banner Header */}
      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center space-x-3">
          <div className={colors.icon}>
            {getIcon(overallSeverity)}
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${colors.text}`}>
              {anomalies.length} Alert{anomalies.length !== 1 ? 's' : ''} Detected
            </h3>
            <p className={`text-xs ${colors.text} opacity-75`}>
              {criticalAnomalies.length > 0 && `${criticalAnomalies.length} critical`}
              {criticalAnomalies.length > 0 && warningAnomalies.length > 0 && ' • '}
              {warningAnomalies.length > 0 && `${warningAnomalies.length} warning`}
              {(criticalAnomalies.length > 0 || warningAnomalies.length > 0) && infoAnomalies.length > 0 && ' • '}
              {infoAnomalies.length > 0 && `${infoAnomalies.length} info`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => { e.stopPropagation(); setIsDismissed(true); }}
            className={`p-1 hover:bg-white/50 rounded-md transition-colors ${colors.icon}`}
          >
            <X className="w-4 h-4" />
          </button>
          <button className={colors.icon}>
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t-2 border-current/10 p-4 space-y-2">
          {anomalies.map((anomaly) => {
            const anomalyColors = getSeverityColor(anomaly.severity);
            return (
              <div
                key={anomaly.id}
                className={`p-3 ${anomalyColors.bg} border ${anomalyColors.border} rounded-lg`}
              >
                <div className="flex items-start space-x-2">
                  <div className={anomalyColors.icon}>
                    {getIcon(anomaly.severity)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className={`text-sm font-semibold ${anomalyColors.text}`}>
                        {anomaly.title}
                      </h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${anomalyColors.badge}`}>
                        {anomaly.severity}
                      </span>
                    </div>
                    <p className={`text-xs ${anomalyColors.text} opacity-90`}>
                      {anomaly.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

